import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AudioWaveform } from '../shared';

// Animated Waveform Background Component
const WaveformBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Multiple wave layers for depth */}
      {[...Array(5)].map((_, layerIndex) => (
        <svg
          key={layerIndex}
          className="absolute w-full h-full"
          style={{ opacity: 0.1 - layerIndex * 0.015 }}
          viewBox="0 0 1440 800"
          preserveAspectRatio="none"
        >
          <motion.path
            d={`M0,${400 + layerIndex * 20} 
                Q360,${300 + layerIndex * 30} 720,${400 + layerIndex * 20} 
                T1440,${400 + layerIndex * 20} 
                V800 H0 Z`}
            fill="none"
            stroke="#FD802E"
            strokeWidth={2 - layerIndex * 0.3}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ 
              pathLength: 1, 
              opacity: 1,
              d: [
                `M0,${400 + layerIndex * 20} Q360,${300 + layerIndex * 30} 720,${400 + layerIndex * 20} T1440,${400 + layerIndex * 20} V800 H0 Z`,
                `M0,${400 + layerIndex * 20} Q360,${500 - layerIndex * 30} 720,${400 + layerIndex * 20} T1440,${400 + layerIndex * 20} V800 H0 Z`,
                `M0,${400 + layerIndex * 20} Q360,${300 + layerIndex * 30} 720,${400 + layerIndex * 20} T1440,${400 + layerIndex * 20} V800 H0 Z`,
              ]
            }}
            transition={{
              pathLength: { duration: 2, delay: layerIndex * 0.2 },
              d: { duration: 4 + layerIndex, repeat: Infinity, ease: "easeInOut" }
            }}
          />
        </svg>
      ))}
      
      {/* Animated spectrum bars at the bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-32 flex items-end justify-center gap-1 opacity-20">
        {[...Array(80)].map((_, i) => (
          <motion.div
            key={i}
            className="w-1 bg-gradient-to-t from-neon-orange/50 to-neon-orange rounded-t"
            initial={{ height: 4 }}
            animate={{ 
              height: [4, Math.random() * 80 + 20, 4],
            }}
            transition={{
              duration: 1 + Math.random() * 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.02,
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Floating AI Nodes Component
const FloatingAINodes = ({ side }) => {
  const nodes = side === 'left' 
    ? [
        { x: 20, y: 100, size: 8 },
        { x: 60, y: 200, size: 6 },
        { x: 40, y: 320, size: 10 },
        { x: 80, y: 450, size: 5 },
        { x: 30, y: 550, size: 7 },
      ]
    : [
        { x: 80, y: 120, size: 7 },
        { x: 40, y: 240, size: 9 },
        { x: 70, y: 380, size: 6 },
        { x: 20, y: 500, size: 8 },
        { x: 50, y: 600, size: 5 },
      ];

  const connections = [
    [0, 1], [1, 2], [2, 3], [3, 4], [0, 2], [1, 3], [2, 4]
  ];

  return (
    <motion.div 
      className={`absolute top-0 ${side === 'left' ? 'left-0' : 'right-0'} w-24 md:w-32 h-full hidden lg:block`}
      initial={{ opacity: 0, x: side === 'left' ? -50 : 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 1, delay: 0.5 }}
    >
      <svg className="w-full h-full" viewBox="0 0 100 700">
        {/* Connection Lines */}
        {connections.map(([from, to], i) => (
          <motion.line
            key={i}
            x1={nodes[from].x}
            y1={nodes[from].y}
            x2={nodes[to].x}
            y2={nodes[to].y}
            stroke="#FD802E"
            strokeWidth="1"
            strokeOpacity="0.3"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.5, delay: 0.8 + i * 0.1 }}
          />
        ))}
        
        {/* Animated data flow on connections */}
        {connections.map(([from, to], i) => (
          <motion.circle
            key={`flow-${i}`}
            r="2"
            fill="#FD802E"
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 1, 0],
              cx: [nodes[from].x, nodes[to].x],
              cy: [nodes[from].y, nodes[to].y],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.3,
              ease: "linear"
            }}
          />
        ))}
        
        {/* Nodes */}
        {nodes.map((node, i) => (
          <motion.g key={i}>
            {/* Outer glow */}
            <motion.circle
              cx={node.x}
              cy={node.y}
              r={node.size + 4}
              fill="none"
              stroke="#FD802E"
              strokeWidth="1"
              strokeOpacity="0.2"
              animate={{ 
                r: [node.size + 4, node.size + 8, node.size + 4],
                strokeOpacity: [0.2, 0.4, 0.2]
              }}
              transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
            />
            {/* Main node */}
            <motion.circle
              cx={node.x}
              cy={node.y}
              r={node.size}
              fill="#233D4C"
              stroke="#FD802E"
              strokeWidth="2"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
            />
            {/* Inner dot */}
            <motion.circle
              cx={node.x}
              cy={node.y}
              r={node.size / 3}
              fill="#FD802E"
              animate={{ 
                opacity: [0.5, 1, 0.5],
                r: [node.size / 3, node.size / 2, node.size / 3]
              }}
              transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.15 }}
            />
          </motion.g>
        ))}
      </svg>
    </motion.div>
  );
};

// Video Modal Component
const VideoModal = ({ isOpen, onClose }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          
          {/* Modal Content */}
          <motion.div
            className="relative z-10 w-full max-w-4xl bg-dark-deeper rounded-2xl overflow-hidden border border-neon-orange/30 shadow-neon"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-dark-base/80 border border-neon-orange/50 flex items-center justify-center text-neon-orange hover:bg-neon-orange hover:text-white transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Video Placeholder */}
            <div className="aspect-video bg-dark-base flex items-center justify-center">
              <div className="text-center">
                <motion.div
                  className="w-24 h-24 mx-auto mb-6 rounded-full bg-neon-orange/20 border-2 border-neon-orange flex items-center justify-center"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <svg className="w-10 h-10 text-neon-orange ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </motion.div>
                <p className="text-gray-400">Demo video placeholder</p>
                <p className="text-gray-500 text-sm mt-2">Replace with actual video embed</p>
              </div>
            </div>
            
            {/* Video Info Bar */}
            <div className="p-4 bg-dark-deeper border-t border-neon-orange/20">
              <h3 className="text-white font-heading font-semibold">SoundFlowAI Platform Demo</h3>
              <p className="text-gray-400 text-sm">See how AI transforms your audio workflow</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const HeroSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const navigate = useNavigate();

  return (
    <section id="hero" className="min-h-screen flex items-center relative overflow-hidden pt-16">
      {/* Left-side hero image (from public/) */}
      <img
        src={"/herobg_result.webp"}
        alt="Hero background"
        className="hidden lg:block absolute left-8 top-24 w-96 rounded-2xl shadow-2xl object-cover z-0"
      />
      {/* Dark gradient base */}
      <div className="absolute inset-0 bg-gradient-radial from-dark-light/50 via-dark-base to-dark-deeper"></div>
      
      {/* Animated Waveform Background */}
      <WaveformBackground />
      
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-grid-pattern bg-[size:50px_50px] opacity-50"></div>
      
      {/* Floating Orbs */}
      <motion.div 
        className="absolute top-20 right-20 w-64 h-64 bg-neon-orange/10 rounded-full blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 4, repeat: Infinity }}
      />
      <motion.div 
        className="absolute bottom-20 left-20 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
        animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 5, repeat: Infinity }}
      />

      {/* Floating AI Nodes - Left Side */}
      <FloatingAINodes side="left" />
      
      {/* Floating AI Nodes - Right Side */}
      <FloatingAINodes side="right" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.span 
                className="inline-block px-4 py-1 bg-neon-orange/20 text-neon-orange text-sm font-semibold rounded-full mb-6 border border-neon-orange/30"
                animate={{ boxShadow: ['0 0 10px rgba(253, 128, 46, 0.3)', '0 0 20px rgba(253, 128, 46, 0.5)', '0 0 10px rgba(253, 128, 46, 0.3)'] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                🎵 AI-Powered Audio Revolution
              </motion.span>
              
              {/* Main Tagline with Neon Glow */}
              <motion.h1 
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold leading-tight mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <motion.span 
                  className="text-transparent bg-clip-text bg-gradient-to-r from-neon-orange to-neon-orange-light inline-block"
                  style={{
                    textShadow: '0 0 40px rgba(253, 128, 46, 0.5)'
                  }}
                  animate={{
                    textShadow: [
                      '0 0 20px rgba(253, 128, 46, 0.3)',
                      '0 0 40px rgba(253, 128, 46, 0.6)',
                      '0 0 20px rgba(253, 128, 46, 0.3)',
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  Revolutionize Your Audio World
                </motion.span>
                <br/>
                <span className="text-white">with AI-Powered</span>
                <br/>
                <motion.span 
                  className="text-neon-orange"
                  animate={{
                    textShadow: [
                      '0 0 10px rgba(253, 128, 46, 0.5)',
                      '0 0 30px rgba(253, 128, 46, 0.8)',
                      '0 0 10px rgba(253, 128, 46, 0.5)',
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                >
                  Automation
                </motion.span>
              </motion.h1>
              
              {/* Subheadline */}
              <motion.p 
                className="text-base sm:text-lg text-gray-400 mb-8 max-w-xl mx-auto lg:mx-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                Streamline audio production, music creation, podcasts, and sound design. Automate, optimize, and manage every step of your audio workflows with VeritoneX.
              </motion.p>
              
              {/* CTA Buttons */}
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                {/* Get Started Button with Pulsing Glow */}
                <motion.button
                  className="group relative px-8 py-4 bg-neon-orange text-white font-bold rounded-full overflow-hidden"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/veritonetech-ai')}
                  animate={{
                    boxShadow: [
                      '0 0 20px rgba(253, 128, 46, 0.4)',
                      '0 0 40px rgba(253, 128, 46, 0.6)',
                      '0 0 20px rgba(253, 128, 46, 0.4)',
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <span className="relative z-10">VeritoneX</span>
                  {/* Shine effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: '100%' }}
                    transition={{ duration: 0.5 }}
                  />
                </motion.button>
                
                
              </motion.div>
            </motion.div>
          </div>

          {/* Hero image replacing Interactive Demo Card */}
          <motion.div
            className="relative flex items-center justify-center"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <img
              src={"/herobg_result.webp"}
              alt="Hero"
              className="w-full max-w-md lg:max-w-2xl rounded-3xl shadow-2xl object-cover"
            />
          </motion.div>
        </div>
        
        {/* Scroll indicator removed per request */}
      </div>
      
      {/* Video Modal */}
      <VideoModal isOpen={showVideo} onClose={() => setShowVideo(false)} />
    </section>
  );
};

export default HeroSection;
